import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DEFAULT_TIPS, PAGE_TIPS, getSessionId, newSessionId, streamChat } from "@/lib/assistant";
import { AssistantText } from "@/components/assistant/AssistantText";
import { SupportOffer } from "@/components/assistant/SupportOffer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, RotateCcw, SendHorizontal, Sparkles } from "lucide-react";

const Bubble = ({ m }) => (m.role === "user" ? (
  <div className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#062a20] px-3.5 py-2 text-sm text-white whitespace-pre-wrap">{m.text}</div></div>
) : (
  <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-white px-3.5 py-2.5 ring-1 ring-zinc-200/80 shadow-sm">
    {m.text ? <AssistantText text={m.text} /> : <span className="inline-flex items-center gap-2 text-xs text-zinc-400"><Loader2 className="h-3.5 w-3.5 animate-spin" />Düşünüyor…</span>}
  </div>
));

export const ChatTab = ({ onTicketCreated }) => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const uid = user?.id || user?._id;
  const [sessionId, setSessionId] = useState(() => getSessionId(uid));
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/history", { params: { session_id: sessionId } })
      .then((r) => { setMsgs(r.data.messages); setRemaining(r.data.remaining); }).catch(() => {});
  }, [sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    const patch = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const end = await streamChat({ sessionId, message: q, page: pathname, onDelta: (d) => patch((x) => ({ ...x, text: x.text + d })) });
      patch((x) => ({ ...x, support: end.support, question: q }));
      if (end.remaining != null) setRemaining(end.remaining);
    } catch (e) {
      patch(() => ({ role: "assistant", text: e.message, support: true, question: q }));
    } finally { setBusy(false); }
  };

  const reset = () => { setSessionId(newSessionId(uid)); setMsgs([]); };
  const tips = PAGE_TIPS[pathname] || DEFAULT_TIPS;
  const limited = remaining === 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3 bg-zinc-50/70" data-testid="assistant-messages">
        {msgs.length === 0 && (
          <div className="space-y-3 animate-in fade-in duration-300">
            <div className="rounded-2xl bg-white p-4 ring-1 ring-zinc-200/80">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900"><Sparkles className="h-4 w-4 text-amber-500" />Merhaba, nasıl yardımcı olabilirim?</p>
              <p className="mt-1 text-xs text-zinc-500">Programın kullanımını anlatırım, kendi rakamlarınıza bakarım, cevaplayamazsam sorunuzu destek ekibine iletirim.</p>
            </div>
            <div className="flex flex-col gap-1.5">
              {tips.map((t, i) => (
                <button key={t} onClick={() => ask(t)} disabled={limited} data-testid={`assistant-tip-${i}`}
                  className="text-left disabled:opacity-50 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[13px] text-emerald-900 hover:bg-emerald-100 transition-colors">{t}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className="space-y-2">
            <Bubble m={m} />
            {m.role === "assistant" && m.support && !busy && <SupportOffer initialSent={m.ticketed} question={m.question || msgs[i - 1]?.text || ""} sessionId={sessionId} page={pathname} onCreated={onTicketCreated} />}
          </div>
        ))}
        {limited && !busy && <SupportOffer question="" sessionId={sessionId} page={pathname} onCreated={onTicketCreated} />}
        <div ref={endRef} />
      </div>
      <div className="border-t border-zinc-200 bg-white p-3">
        <div className="flex items-end gap-2">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} maxLength={2000}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
            disabled={limited} placeholder={limited ? "Bugünkü soru hakkınız doldu" : "Sorunuzu yazın…"} data-testid="assistant-input" className="min-h-[40px] max-h-28 resize-none text-sm" />
          <Button size="icon" onClick={() => ask()} disabled={busy || limited || !input.trim()} data-testid="assistant-send" className="h-10 w-10 shrink-0 bg-emerald-700 hover:bg-emerald-800 text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
          </Button>
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-400">
          <span data-testid="assistant-remaining">{remaining != null ? `Bugün kalan soru hakkı: ${remaining}` : ""}</span>
          {msgs.length > 0 && <button onClick={reset} disabled={busy} data-testid="assistant-new-chat" className="inline-flex items-center gap-1 hover:text-zinc-700"><RotateCcw className="h-3 w-3" />Yeni sohbet</button>}
        </div>
      </div>
    </div>
  );
};
